<template>
  <Transition :name="transition" @enter="onEnter">
    <div
      v-show="isOpen"
      class="navbar-flyout"
      :class="{ 'is-open': isOpen, 'has-transition': hasTransition }"
      :style="{ height: isUntilDesktop ? '100%' : height + 'px' }"
      @enter="onEnter"
    >
      <div ref="inner" class="navbar-flyout-inner">
        <div class="navbar-flyout-title desktop:hidden">
          {{ title }}
        </div>
        <div class="navbar-flyout-content">
          <slot />
        </div>
      </div>
    </div>
  </Transition>
</template>

<script lang="ts" setup>
import { provide } from 'vue'
const props = defineProps<{
  title: string
  isOpen: boolean
  activeFirstLevel: number
}>()

const inner = ref<HTMLElement | null>(null)
const height = ref(0)
const hasTransition = ref(false)

const { isUntilDesktop } = useViewport()

const transition = computed(() => {
  if (!isUntilDesktop.value) {
    if (hasTransition.value) {
      return 'flyout'
    }
  } else {
    return 'flyout'
  }

  return ''
})

function onEnter() {
  nextTick(() => {
    if (inner.value?.clientHeight) {
      height.value = inner.value.clientHeight
    }
  })
}

watch(
  () => props.activeFirstLevel,
  (newValue, oldValue) => {
    if (!isUntilDesktop.value) {
      if (oldValue === 0 || newValue === 0) {
        hasTransition.value = true
      } else {
        hasTransition.value = false
      }
    }
  },
)

function updateFlyoutHeight() {
  onEnter()
}

provide('updateFlyoutHeight', updateFlyoutHeight)
</script>

<style>
@reference '~/assets/css/tailwind.css';
.navbar-flyout {
  @apply absolute top-0 left-0 w-full overflow-hidden bg-background;
  z-index: -1;

  @variant desktop {
    width: 1288px;
    left: -5%;

    .is-default & {
      min-height: 400px;
    }
  }

  @variant belowDesktop {
    z-index: 150;
    top: 0;
    bottom: 0;
    left: initial;
    width: calc(100% - 3rem);
    right: 0;
    overflow: auto;
    overflow-x: hidden;
  }
}

@variant belowDesktop {
  .flyout-enter-from {
    transform: translateX(100%);
  }
  .flyout-enter-active {
    transition: transform 0.5s ease;
  }
  .flyout-enter-to {
    transform: translateX(0);
  }

  .flyout-leave-from {
    transform: translateX(0);
  }
  .flyout-leave-active {
    transition: transform 0.5s ease;
  }
  .flyout-leave-to {
    transform: translateX(100%);
  }
}

@variant desktop {
  .flyout-enter-active,
  .flyout-leave-active {
    transition: 0.5s;
    transition-timing-function: cubic-bezier(0.53, 0.2, 0.24, 1.02);
    .navbar-flyout-inner {
      transition: 0.5s;
      transition-timing-function: cubic-bezier(0.53, 0.2, 0.24, 1.02);
    }
  }
  .flyout-enter,
  .flyout-leave-to {
    height: 0px !important;
    .navbar-flyout-inner {
      transform: translateY(-3rem);
      opacity: 0;
    }
  }
}

.navbar-flyout-title {
  @apply mb-size-6 border-b border-solid border-border pb-size-6 text-size-5 text-scheme-invert;
}

.navbar-flyout-inner {
  padding: 1rem;
  padding-bottom: 6rem;
  @variant tablet {
    padding-top: 5rem;
  }

  @apply desktop:absolute desktop:top-0 desktop:left-0 desktop:w-full;
  @variant desktop {
    transition: 0.5s;
    padding: 2.5rem 3rem;
    padding-top: 7.5rem;
  }
}
</style>
